import type { HttpClientConfig, RequestMethod, FetchOptions, FetchClient } from './types';

/**
 * HTTP Client implementation providing a lightweight wrapper around fetch API
 * with additional features like timeout, error handling, and request formatting.
 */
export class HttpClient implements FetchClient {
  private config: HttpClientConfig;

  /**
   * Creates a new HTTP client instance
   * @param config Configuration options including baseURL, timeout, and default headers
   */
  constructor(config: HttpClientConfig) {
    this.config = {
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      ...config,
    };
  }

  get(url: string, options?: FetchOptions): Promise<Response> {
    return this.request('GET', url, options);
  }

  post(url: string, options?: FetchOptions): Promise<Response> {
    return this.request('POST', url, options);
  }

  put(url: string, options?: FetchOptions): Promise<Response> {
    return this.request('PUT', url, options);
  }

  patch(url: string, options?: FetchOptions): Promise<Response> {
    return this.request('PATCH', url, options);
  }

  delete(url: string, options?: FetchOptions): Promise<Response> {
    return this.request('DELETE', url, options);
  }

  /**
   * Core request method handling timeouts and error conversions
   * @param method HTTP method to use
   * @param url Request URL (will be joined with baseURL)
   * @param options Request options
   */
  private async request(method: RequestMethod, url: string, options: FetchOptions = {}): Promise<Response> {
    const mergedOptions = {
      ...this.config.options,
      ...options,
    };
    // Set up abort controller for timeout handling
    const controller = new AbortController();
    const timeout = this.config.timeout;
    let timeoutId: NodeJS.Timeout | undefined;

    if (timeout) {
      timeoutId = setTimeout(() => {
        controller.abort();
      }, timeout);
    }

    try {
      return await this.executeRequest(method, url, mergedOptions, controller);
    } catch (error) {
      // Convert AbortError to a more user-friendly timeout error
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  /**
   * Executes the actual HTTP request with properly formatted parameters
   */
  private async executeRequest(
    method: RequestMethod,
    url: string,
    options: FetchOptions,
    controller: AbortController,
  ): Promise<Response> {
    const urlWithParams = this.appendQueryParams(url, options?.params);

    const { preparedBody, adjustedHeaders } = this.prepareRequestBodyAndHeaders(options?.body, options?.headers);
    const requestOptions = this.prepareRequestOptions(method, options, preparedBody, adjustedHeaders, controller);

    const fullUrl = this.config.baseURL ? this.config.baseURL + urlWithParams : urlWithParams;

    const response = await fetch(fullUrl, requestOptions);

    if (!response.ok) {
      throw await this.createErrorFromResponse(response);
    }

    return response;
  }

  /**
   * Prepares request body and headers based on content type
   * Handles different body formats: JSON, FormData, URLSearchParams, etc.
   *
   * @param body The request body to prepare
   * @param requestHeaders Custom headers to apply
   * @returns Prepared body and adjusted headers
   */
  private prepareRequestBodyAndHeaders(
    body: unknown,
    requestHeaders?: HeadersInit,
  ): { preparedBody: unknown; adjustedHeaders: Headers } {
    // Start with default headers and merge in any custom headers
    const headers = new Headers(this.config.headers);
    if (requestHeaders) {
      new Headers(requestHeaders).forEach((value, key) => {
        headers.set(key, value);
      });
    }

    // Early return if no body is provided
    if (!body) {
      return { preparedBody: undefined, adjustedHeaders: headers };
    }

    // Special handling for FormData - browser will set the correct Content-Type with boundary
    if (body instanceof FormData) {
      headers.delete('Content-Type');
      return { preparedBody: body, adjustedHeaders: headers };
    }

    // Pass through URLSearchParams as is
    if (body instanceof URLSearchParams) {
      return { preparedBody: body, adjustedHeaders: headers };
    }

    const contentType = this.getContentTypeFromHeaders(headers);

    // If Content-Type is missing, default to application/json
    if (!contentType) {
      headers.set('Content-Type', 'application/json');
      return {
        preparedBody: JSON.stringify(body),
        adjustedHeaders: headers,
      };
    }

    // Handle JSON content type
    if (contentType.includes('application/json')) {
      return {
        preparedBody: JSON.stringify(body),
        adjustedHeaders: headers,
      };
    }

    // Handle form-urlencoded content type
    if (contentType.includes('application/x-www-form-urlencoded')) {
      return {
        preparedBody: this.createUrlSearchParams(body as Record<string, unknown>),
        adjustedHeaders: headers,
      };
    }

    // Default to JSON serialization for other content types
    return {
      preparedBody: JSON.stringify(body),
      adjustedHeaders: headers,
    };
  }

  /**
   * Prepares the final request options for fetch API
   */
  private prepareRequestOptions(
    method: RequestMethod,
    options: FetchOptions,
    body: unknown,
    headers: Headers,
    controller: AbortController,
  ): RequestInit {
    const requestOptions: RequestInit = {
      ...options,
      method,
      signal: controller.signal,
      body: body as BodyInit,
      headers,
    };

    if (options?.next) {
      requestOptions.next = options.next;
    }

    return requestOptions;
  }

  /**
   * @param response The error response from fetch
   */
  private async createErrorFromResponse(response: Response): Promise<never> {
    if (!response) {
      throw new Error('Invalid response object');
    }

    throw response;
  }

  /**
   * Appends query parameters to the URL
   * @param url Base URL
   * @param params Query parameters to append
   */
  private appendQueryParams(url: string, params?: Record<string, string | number | boolean>): string {
    if (!params) {
      return url;
    }

    const queryParams = this.createUrlSearchParams(params);
    const queryString = queryParams.toString();

    if (queryString) {
      return `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
    }

    return url;
  }

  /**
   * Converts an object to URLSearchParams for query strings or form data
   * @param params Object containing key-value pairs to convert
   */
  private createUrlSearchParams(params: Record<string, unknown>): URLSearchParams {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    return searchParams;
  }

  /**
   * Gets the Content-Type header value from Headers object
   */
  private getContentTypeFromHeaders(headers: Headers): string | null {
    return headers.get('Content-Type');
  }
}

/**
 * Factory function to create a new HTTP client
 * @param config Client configuration
 */
export const createHttpClient = (config: HttpClientConfig): FetchClient => {
  return new HttpClient(config);
};
